import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';

import {
  Wrapper,
  Plan,
  Name,
  Block,
  Label,
  Value,
  Btn,
  ActionsWrapper,
  NewSubscriptionContainer,
} from './payments.style';
import {
  cancelSubscription,
  resumeSubscription,
  deleteSubscriptionInfo,
  getSubscriptionInfo,
  SubscriptionStatusType,
  updateSubscriptionInfo,
  ButtonConfig,
} from '../../../apis/subscription';
import Button from '../../../components/Button/Button';
import ModalSubscription from '../../../layout/Modal/ModalSubscription/ModalSubscription';
import useOnClickOutside from '../../../layout/Modal/useClickOutsideModal';
import { dollarSignDecorator } from '../../../shared/utils/common';
import { useAppSelector } from '../../../store/hooks';

const subscriptionLabelHash = {
  inactive: 'Inactive',
  active: 'Active',
  on_grace_period: 'Grace period',
  on_trial: 'Trial',
};

const Payments: React.FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const refSubscription = useRef<any>();

  const { settings } = useAppSelector((state: { settings: any }) => state.settings);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : 'unset';
  }, [open]);

  useOnClickOutside(refSubscription, () => setOpen(false));

  const { isLoading, error, data, refetch } = useQuery('subscriptionInfo', () => getSubscriptionInfo());

  if (isLoading || error || !data) {
    return null;
  }

  const statusLabel =
    data?.status in subscriptionLabelHash
      ? subscriptionLabelHash[data?.status]
      : subscriptionLabelHash[SubscriptionStatusType.inactive];

  const buttonHash = {
    checkout: {
      text: 'New Subscription',
      action: () =>
        updateSubscriptionInfo().then((res) => {
          window.open(res.url, '_blank');
        }),
    },
    deleteImmediately: {
      text: 'Delete immediately',
      action: () =>
        deleteSubscriptionInfo().then(() => {
          refetch();
        }),
    },
    cancelSubscription: {
      text: 'Cancel',
      action: () =>
        cancelSubscription().then(() => {
          refetch();
        }),
    },
    resumeSubscription: {
      text: 'Resume',
      action: () =>
        resumeSubscription().then((res) => {
          refetch();
        }),
    },
  };

  const statusButtonsMap: Partial<Record<SubscriptionStatusType, ButtonConfig[]>> = {
    [SubscriptionStatusType.gracePeriod]: [buttonHash.resumeSubscription],
    [SubscriptionStatusType.active]: [buttonHash.cancelSubscription],
    [SubscriptionStatusType.onTrial]: [buttonHash.cancelSubscription],
    [SubscriptionStatusType.inactive]: [buttonHash.checkout],
  };

  const defaultButtons: ButtonConfig[] = [buttonHash.checkout];

  const getButtonsForStatus = (status?: SubscriptionStatusType): ButtonConfig[] => {
    if (!status) {
      return [];
    }

    return statusButtonsMap[status] ?? defaultButtons;
  };

  const buttons = getButtonsForStatus(data?.status);

  const terminalNameContent = () => {
    if (!data?.subscription) {
      return null;
    }
    const quantity = data?.subscription?.quantity;
    const price = 30;

    return (
      <Plan>
        <Name>Terminal Name</Name>
        <Block>
          <Label>Type:</Label>
          <Value>{`Price: ${dollarSignDecorator(price)}/driver`}</Value>
        </Block>
        <Block>
          <Label>Drivers:</Label>
          <Value>{quantity}</Value>
        </Block>
        <Block style={{ marginBottom: 0 }}>
          <Label>Total price:</Label>
          <Value>{dollarSignDecorator(quantity * price)}/mo</Value>
        </Block>
      </Plan>
    );
  };

  const paymentInfoContent = () => {
    if (
      [SubscriptionStatusType.active, SubscriptionStatusType.gracePeriod, SubscriptionStatusType.onTrial].includes(
        data?.status
      )
    ) {
      return (
        <Plan>
          <Name>Payment Information</Name>
          <Block>
            <Label>Status:</Label>
            <Value>{statusLabel}</Value>
          </Block>
          {data?.next_payment_amount ? (
            <Block>
              <Label>Next payment amount:</Label>
              <Value>
                {data?.next_payment_amount} {data?.currency}
              </Value>
            </Block>
          ) : null}
          {data.status === SubscriptionStatusType.onTrial && data.trial_ends_at ? (
            <Block>
              <Label>Trials end date:</Label>
              <Value>{data?.trial_ends_at ?? '-'}</Value>
            </Block>
          ) : data.status === SubscriptionStatusType.active ? (
            <Block>
              <Label>Next payment date:</Label>
              <Value>{data?.next_payment_date ?? '-'}</Value>
            </Block>
          ) : (
            <Block>
              <Label>Expiration date:</Label>
              <Value>{data?.current_period_end ?? '-'}</Value>
            </Block>
          )}
          <Block style={{ margin: '32px 0 0', justifyContent: 'flex-end' }}>
            <ActionsWrapper>
              {data?.next_payment_date ? (
                buttons.map((button, idx) => (
                  <Button key={idx} handleButton={button.action} pad="8px 24px" text={button.text} />
                ))
              ) : data.status === 'inactive' ? (
                <Button handleButton={buttonHash.checkout.action} pad="8px 24px" text={buttonHash.checkout.text} />
              ) : (
                <Button
                  handleButton={buttonHash.resumeSubscription.action}
                  pad="8px 24px"
                  text={buttonHash.resumeSubscription.text}
                />
              )}
            </ActionsWrapper>
          </Block>
        </Plan>
      );
    }

    return null;
  };

  return (
    <Wrapper>
      {terminalNameContent()}
      {paymentInfoContent()}
      {data?.status === SubscriptionStatusType.inactive && (
        <NewSubscriptionContainer>
          <Button handleButton={buttonHash.checkout.action} pad="8px 24px" text={buttonHash.checkout.text} />
        </NewSubscriptionContainer>
      )}
      {open && <ModalSubscription refSubscription={refSubscription} close={() => setOpen(false)} />}
    </Wrapper>
  );
};

export default Payments;
